import { datadogRum } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';
import { POST } from '@carvana/core-http';
import { CORRELATION_ID_HEADER, getOrCreateSessionId } from '@utils/session-id';
import { getApiBaseUrl } from '../base-url-config';

type UserProfileAddress = {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip5?: string;
  addressAttributes?: {
    [key: string]: boolean;
  };
};

type AddressGroup = {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip5?: string;
};

export type AutocompleteAddress = {
  fullSuggestedAddress: string;
  addressGroup: AddressGroup;
};

type UserProfileResponse = {
  data: {
    userProfile: {
      profile: {
        addresses: {
          [key: string]: UserProfileAddress;
        };
      };
    };
  };
};

export const UserProfile = {
  fetchAddress: async (): Promise<AutocompleteAddress | null> => {
    const { user } = getUserState();
    const { user_id: userId } = user;
    const ddAction = 'fetch-user-address-details';
    datadogRum.addAction(ddAction, { type: 'start' });

    const userProflileUrl = `${getApiBaseUrl()}/core/user-profile-api/api/graph`;

    try {
      const response = await POST(
        userProflileUrl,
        {
          query: `
            query UserProfile($userId: ID!) {
              userProfile(userId: $userId) {
                profile {
                  addresses {
                    home {
                      street
                      street2
                      city
                      state
                      zip5
                    }
                    delivery {
                      street
                      street2
                      city
                      state
                      zip5
                    }
                  }
                }
              }
            }
          `,
          variables: { userId },
        },
        {
          headers: {
            Accept: '*/*',
            'apollographql-client-name': 'mycars-sars',
            'apollographql-client-version': '1.0',
            'Content-Type': 'application/json',
            [CORRELATION_ID_HEADER]: getOrCreateSessionId(),
          },
          authorized: true,
        }
      );

      const data: UserProfileResponse = await response.json();

      if (!response.ok) {
        const { message } = (data as any) || {};
        datadogRum.addAction(ddAction, { type: 'error', error: message });
        return null;
      }

      const { addresses } = data?.data?.userProfile?.profile || {};
      const isHomePoBox = /p\.*o\.*\s*box/i.test(addresses?.home?.street || '');
      const addressToUse = isHomePoBox ? addresses?.delivery : addresses?.home;

      if (!addressToUse) {
        datadogRum.addAction(ddAction, { type: 'error', error: 'No address found' });
        return null;
      }

      datadogRum.addAction(ddAction, { type: 'success', address: addressToUse?.street });
      return {
        fullSuggestedAddress: `${addressToUse?.street}, ${addressToUse?.city}, ${addressToUse?.state} ${addressToUse?.zip5}`,
        addressGroup: {
          streetAddress: addressToUse?.street,
          city: addressToUse?.city,
          state: addressToUse?.state,
          zip5: addressToUse?.zip5,
        },
      };
    } catch (e) {
      datadogRum.addAction(ddAction, { type: 'error', error: e });
      return null;
    }
  },
};

export default UserProfile;
